import React, { useEffect, useState } from 'react';
import { useAuth } from 'services/auth-service';
import Modal from 'components/elements/Modal';
import * as yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Input from 'components/elements/Form/Input/input';
import ResourcesIcon from 'assets/Icons/sidebarIcons/ResourcesIcon';
import { useQuery } from 'react-query';
import { ResourceProps } from 'services/hooks/resource-service';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import useSaveResource from './crud/useSave';
import BuildingService from 'services/hooks/building-service';
import SelectEntity from 'components/structure/Common/ListsEntity/SelectEntity';
import SelectInput from 'components/elements/Form/select/selectInput';
import InputDatePicker from 'components/elements/Form/Input/inputDatePicker';
import { format, addMinutes, startOfDay, setHours, setMinutes, setSeconds } from 'date-fns';
import { getDateFromTime } from 'utils/time';
import PrimaryButton from 'components/elements/Form/button/primaryButton';
import SpaceService, { SpaceProps } from 'services/hooks/space-service';
import { toast } from 'react-toastify';
import {
  generateApiFailErrorMessage,
  generateSaveErrorMessage,
  generateSaveSuccessMessage,
  generateUpdateErrorMessage,
  generateUpdateSuccessMessage
} from 'utils/helpers';
import { AmenityTypeOptions, GetNonServerErrorMessage } from 'utils/constants';
import variables from 'variables';
import ImageUploadInput from 'components/elements/Form/Input/ImageUploadInput';
import { SelectOption } from 'utils/types';

function getNextMultipleOf15Minutes(date: Date) {
  const minutes = date.getMinutes();
  const remainder = minutes % 15;
  const minutesToAdd = remainder === 0 ? 15 : 15 - remainder;
  const nextTime = addMinutes(date, minutesToAdd);

  if (nextTime.getDate() !== date.getDate()) {
    return startOfDay(nextTime);
  }

  return nextTime;
}

type CreateProps = {
  entityName: string;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setEditUUID?: React.Dispatch<React.SetStateAction<string>>;
  createQuery: (newResource: ResourceProps) => Promise<ResourceProps>;
  updateQuery: (uuid: string, updatedResource: ResourceProps) => Promise<ResourceProps>;
  editQuery?: (uuid: string | undefined) => Promise<ResourceProps>;
  editUUID?: string;
  queryName: string;
};

/* The code block is defining a validation schema using the `yup` library for a form in a React component. 
If these validation rules are not met, an error message will be displayed.*/
const CreateResourceSchema = yup.object().shape({
  name: yup.string().required('Name is required'),
  description: yup.string().required('Description is required'),
  type: yup.string().required('Type is required'),
  open: yup.date().required('Open time is required'),
  close: yup
    .date()
    .required('Close time is required')
    .test('is-after', 'Latest booking time must be after earliest booking time', function (value) {
      const { open } = this.parent;
      if (!value || !open) return true;
      const openDate = new Date(open);
      const closeDate = new Date(value);
      return closeDate > openDate;
    }),
  buildingUuid: yup.string().required('Building is required'),
  image: yup.mixed().test('fileRequired', 'Image is required', (value) => {
    return value && value !== undefined;
  })
});

export default function Save({
  entityName,
  showModal,
  setEditUUID,
  setShowModal,
  createQuery,
  updateQuery,
  editQuery,
  editUUID,
  queryName
}: CreateProps) {
  const {
    state: { user }
  } = useAuth();

  const [getResourceResult, setGetResourceResult] = useState<ResourceProps | null>(null);
  const [spaceOptions, setSpaceOptions] = useState<SelectOption[]>([]);
  const [disabled, setDisabled] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const { isSuccess, data, refetch } = useQuery<ResourceProps | undefined, Error>(
    'resource',
    () => (editUUID ? editQuery?.(editUUID) : undefined),
    {
      enabled: editUUID ? true : false,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Amenity'));
      }
    }
  );

  const { data: spacesData } = useQuery<SpaceProps[], Error>('spaces', SpaceService.findAllSpaces, {
    enabled: user?.role === 'SUPER' || user?.role === 'ACCOUNT' || user?.role === 'BUILDING',
    onError: () => {
      toast.error(generateApiFailErrorMessage('Spaces'));
    }
  });

  /* The `useForm` hook from the `react-hook-form` library is being used to create a form with form validation. */
  const methods = useForm({
    defaultValues: getResourceResult
      ? {
          uuid: getResourceResult?.uuid,
          name: getResourceResult?.name,
          type: getResourceResult?.type,
          description: getResourceResult?.description,
          close: getDateFromTime(getResourceResult?.close),
          open: getDateFromTime(getResourceResult?.open),
          buildingUuid: getResourceResult?.buildingUuid,
          image: getResourceResult?.image,
          spaceUuid: getResourceResult?.spaceUuid
        }
      : {
          uuid: '',
          name: '',
          type: '',
          open: setSeconds(setMinutes(setHours(new Date(), 9), 0), 0),
          close: setSeconds(setMinutes(setHours(new Date(), 17), 0), 0),
          description: '',
          buildingUuid: user?.role === 'BUILDING' ? user.uuid : '',
          image: '',
          spaceUuid: ''
        },
    resolver: yupResolver(CreateResourceSchema)
  });

  useEffect(() => {
    if (data && editUUID) {
      refetch();
      setGetResourceResult(data);
      // Set image on Edit mode for image preview
      setSelectedImage(data.image ? variables.RESOURCE_IMAGE_URL + data.image : null);
      /* `methods.reset()` is being used to set the initial values of the form fields to the values retrieved from the `data` object. The 
      `data` object contains the Resource details fetched from the API when editing an existing Resource. By calling `methods.reset()` with the 
      desired initial values, the form fields will be populated with the fetched data when the form is rendered. */
      methods.reset({
        uuid: data?.uuid,
        name: data?.name,
        type: data?.type,
        description: data?.description,
        close: getDateFromTime(data?.close),
        open: getDateFromTime(data?.open),
        buildingUuid: data?.buildingUuid,
        image: data?.image,
        spaceUuid: data?.spaceUuid
      });
    }
  }, [data, editUUID, methods, refetch]);

  /* The `saveResourceMutation` variable is created using the `useSaveResource` hook. This hook is used to handle the mutation (create or update) 
  of a Resource. It takes in several parameters: */
  const saveResourceMutation = useSaveResource({
    createQuery,
    updateQuery,
    queryName,
    onSuccess: () => {
      setShowModal(false);
      setDisabled(false);
      if (!editUUID) {
        toast.success(generateSaveSuccessMessage('Amenity'));
      } else {
        toast.success(generateUpdateSuccessMessage('Amenity'));
      }
      if (setEditUUID) setEditUUID('');
    },
    onError: (error: any) => {
      setDisabled(false);
      if (error.response) {
        if (!editUUID) {
          toast.error(generateSaveErrorMessage('Amenity'));
        } else {
          toast.error(generateUpdateErrorMessage('Amenity'));
        }
      } else {
        // Handle cases where the error is not from the server response
        toast.error(GetNonServerErrorMessage);
      }
    }
  });

  const buildingWatch = methods.watch('buildingUuid');
  const spaceWatch = methods.watch('spaceUuid');
  const openTimeWatch = methods.watch('open');

  const minCloseTime = openTimeWatch
    ? getNextMultipleOf15Minutes(new Date(openTimeWatch))
    : undefined;

  // Set minTime to the same value as maxTime to make all options unavailable
  const adjustedMinCloseTime =
    openTimeWatch &&
    new Date(openTimeWatch).getHours() === 23 &&
    new Date(openTimeWatch).getMinutes() === 45
      ? new Date(0, 0, 0, 23, 59)
      : minCloseTime;

  // Filter space options if building is selected
  useEffect(() => {
    if (spacesData) {
      if (buildingWatch) {
        const filteredSpaces = spacesData.filter(
          (space) => space.buildingUuid === buildingWatch && space.uuid !== undefined
        );
        setSpaceOptions(
          filteredSpaces.map((space) => ({
            value: space.uuid as string,
            label: space.name
          }))
        );
      } else {
        const validSpaces = spacesData.filter((space) => space.uuid !== undefined);
        setSpaceOptions(
          validSpaces.map((space) => ({
            value: space.uuid as string,
            label: space.name
          }))
        );
      }
    }
  }, [spacesData, buildingWatch]);

  // Reset space when the selected building changes and the space does not belong to that building.
  useEffect(() => {
    if (spacesData && buildingWatch) {
      const selectedSpace = spacesData.find((space) => space.uuid === spaceWatch);
      if (selectedSpace && selectedSpace.buildingUuid !== buildingWatch) {
        methods.setValue('spaceUuid', '');
      }
    }
  }, [spacesData, buildingWatch, spaceWatch, methods]);

  /* The function onSubmit is used to save Resource information and disable the submit button.
   * The parameter `e` is of type `ResourceProps`. It is used to represent the event object that is passed to the `onSubmit` function. */
  const onSubmit = async (e: ResourceProps) => {
    setDisabled(true);
    const openTime = format(new Date(e?.open), 'HH:mm:ss');
    const closeTime = format(new Date(e?.close), 'HH:mm:ss');

    saveResourceMutation.mutate({
      uuid: e?.uuid,
      name: e?.name,
      type: e?.type,
      description: e?.description,
      image: e?.image,
      buildingUuid: user?.role === 'BUILDING' ? user?.uuid : e?.buildingUuid,
      open: openTime,
      close: closeTime,
      spaceUuid: e?.spaceUuid
    });
  };

  return !editUUID || isSuccess ? (
    <>
      <Modal
        isOpen={showModal}
        onRequestClose={() => {
          setShowModal(false);
          if (setEditUUID) setEditUUID('');
        }}
        title={entityName}
      >
        <form onSubmit={methods.handleSubmit(onSubmit)} className={`text-left items-start`}>
          <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4 mr-8'}>
            {(user.role === 'SUPER' || user.role === 'ACCOUNT') && (
              <SelectEntity
                Controller={Controller}
                control={methods.control}
                label="Choose a Building*"
                placeholder="Select Building"
                queryname="buildings"
                name="buildingUuid"
                errors={methods.formState.errors}
                disabled={editUUID && true}
                query={BuildingService.findAllBuildings}
                defaultValue=""
              />
            )}
            <SelectInput
              Controller={Controller}
              control={methods.control}
              label="Choose a Space"
              placeholder="Select Space"
              name="spaceUuid"
              errors={methods.formState.errors}
              disabled={false}
              options={spaceOptions}
            />
            <Input
              label="Amenity Name*"
              name="name"
              type="text"
              placeholder="Bline"
              autoFocus
              methods={methods}
            />
            <SelectInput
              Controller={Controller}
              control={methods.control}
              label="Type*"
              name="type"
              defaultValue={getResourceResult?.type}
              errors={methods.formState.errors}
              placeholder="Select a type"
              options={AmenityTypeOptions}
            />
            <Input
              label="Description*"
              name="description"
              type="text"
              placeholder="Bline"
              methods={methods}
            />
            <InputDatePicker
              Controller={Controller}
              control={methods.control}
              label="Earliest Booking*"
              name="open"
              placeholder="Select time"
              isTime
              isClearable
              error={methods.formState.errors.open && methods.formState.errors.open.message}
            />
            <InputDatePicker
              Controller={Controller}
              control={methods.control}
              label="Latest Booking*"
              name="close"
              placeholder="Select time"
              isTime
              isClearable
              minTime={adjustedMinCloseTime}
              maxTime={new Date(0, 0, 0, 23, 59)}
              error={methods.formState.errors.close && methods.formState.errors.close.message}
            />
            <ImageUploadInput
              methods={methods}
              selectedImage={selectedImage}
              setSelectedImage={setSelectedImage}
              name="image"
              label="Image*"
              ratioInfo="For best display, use images with ~3:1 ratio (e.g., 3024px x 956px)."
              error={methods.formState.errors.image}
            />
          </div>
          <PrimaryButton
            buttonLabel={entityName}
            iconLeft={<ResourcesIcon />}
            variant="default"
            size={'mt-4 px-6 w-auto gap-2'}
            disabled={disabled}
          />
        </form>
      </Modal>
    </>
  ) : (
    <LoadingSpinner />
  );
}
